import { z } from 'zod';
import type { DimensionValueToken } from '@sleed/bizcommerce-api';

/**
 * Dimension value edit form. Mirrors `DimensionValueToken` and the legacy admin popup
 * (Core/client/admin/src/pages/Catalog/DimensionValues/index.js): did (parent dimension) required,
 * vtxt (value text) required, optional color (chex), code (cd), ERP sync code (erpcd ≤50),
 * alternate texts (vtxt_a1/vtxt_a2), grouping (dvg), iv boolean, si required, plus a single image (img1).
 * `img1File`/`imgdel1` carry the image upload/delete intent (committed out-of-band after save).
 */
export const dimensionValueSchema = z.object({
	id: z.number().optional(),
	did: z.number(),
	vtxt: z.string().trim().min(1, 'validation.required'),
	cd: z.string().trim().nullable(),
	erpcd: z.string().trim().max(50, 'validation.tooLong').nullable(),
	chex: z.string().trim().nullable(),
	vtxt_a1: z.string().trim().nullable(),
	vtxt_a2: z.string().trim().nullable(),
	dvg: z.number().nullable(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	img1: z.string().nullable(),
	img1File: z.instanceof(File).nullable().optional(),
	imgdel1: z.boolean()
});

export type DimensionValueFormValues = z.infer<typeof dimensionValueSchema>;

export function emptyDimensionValue(did: number): DimensionValueFormValues {
	return {
		did,
		vtxt: '',
		cd: '',
		erpcd: '',
		chex: null,
		vtxt_a1: '',
		vtxt_a2: '',
		dvg: null,
		iv: true,
		si: 0,
		img1: null,
		img1File: null,
		imgdel1: false
	};
}

export function tokenToForm(token: DimensionValueToken): DimensionValueFormValues {
	return {
		id: token.id,
		did: token.did,
		vtxt: token.vtxt ?? '',
		cd: token.cd ?? '',
		erpcd: token.erpcd ?? '',
		chex: token.chex ?? null,
		vtxt_a1: token.vtxt_a1 ?? '',
		vtxt_a2: token.vtxt_a2 ?? '',
		dvg: token.dvg ?? null,
		iv: token.iv ?? false,
		si: token.si ?? 0,
		img1: token.img1 ?? null,
		img1File: null,
		imgdel1: false
	};
}

export function formToToken(form: DimensionValueFormValues): DimensionValueToken {
	return {
		id: form.id,
		did: form.did,
		vtxt: form.vtxt.trim(),
		cd: form.cd?.trim() || null,
		erpcd: form.erpcd?.trim() || null,
		chex: form.chex?.trim() || null,
		vtxt_a1: form.vtxt_a1?.trim() || null,
		vtxt_a2: form.vtxt_a2?.trim() || null,
		dvg: form.dvg,
		iv: form.iv,
		si: form.si,
		img1: form.img1,
		imgdel1: form.imgdel1
	};
}
